import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { select, NgRedux } from "@angular-redux/store";
import { Observable, Subscription as RxSubscription } from "rxjs";
import { MatSort, MatPaginator, MatTableDataSource } from "@angular/material";
import { ApiResponseDispatcher } from "../../../store/api-response/api-response-dispatcher";
import { TranslationService } from "../../../common/services/languages/translation.service";
import { OrchestrationService } from "../../services/orchestration-service/orchestration.service";
import { ProvisioningHistory } from "./models/provisioning-history.model";
import { SidenavService } from "../../services/sidenav.service";
import { AppState } from "../../../store/state.model";
import { transformDateTime } from "../../../common/helpers/utils";
import { ApiResponseState } from "../../../store/api-response/api-response-state.model";
import { ApiResponseActions } from "../../../store/api-response/api-response-actions";
import { MatButton } from "@angular/material/button";
import { NgIf, NgFor } from "@angular/common";
import { MatFormField, MatLabel } from "@angular/material/select";
import { MatInput } from "@angular/material/input";
import { FormsModule } from "@angular/forms";
import { MatPaginator as MatPaginator_1 } from "@angular/material/paginator";
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from "@angular/material/table";
import { MatSort as MatSort_1, MatSortHeader } from "@angular/material/sort";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "app-provisioning-history",
    templateUrl: "./provisioning-history.component.html",
    styleUrls: ["./provisioning-history.component.scss"],
    imports: [MatButton, NgIf, MatFormField, MatLabel, MatInput, FormsModule, MatPaginator_1, MatTable, MatSort_1, NgFor, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, TranslatePipe]
})
export class ProvisioningHistoryComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  /**
   * @Property to show or hide the table
   **/
  public showTable = false;
  /**
   * @Property to hold the displayed columns
   **/
  public displayedColumns: string[];
  /**
   * @Property to hold the data source
   **/
  public dataSource: MatTableDataSource<ProvisioningHistory>;
  /**
   * @Property total number of provisioning history records
   **/
  public totalCount = 0;
  /**
   * @Property number of records per page
   **/
  public pageSize = 10;
  /**
   * @Property current page index
   **/
  public currentPage = 0;
  /**
   * @Property filter text for the table
   **/
  public filter = "";
  /**
   * @arg paginator paginator
   */
  public paginator;
  /**
   * @Property to hold the provision history
   **/
  public provisionHistory: any;
  /**
   * @Property to hold the details of the row
   **/
  public vehicleRowDetails: string;
  /**
   * @Property to hold the details of the selected element
   **/
  public selectedElement: object;
  /**
   * @Property observable for API response state
   **/
  @select("api") readonly apiObservable: Observable<ApiResponseState>;
  /**
   * @arg content view page element
   */
  @ViewChild(MatPaginator, { static: false } as any) set matpaginator(
    paginator: ElementRef
  ) {
    this.paginator = paginator;
  }
  /**
   * @arg sort sort
   */
  public sort;
  /**
   * @Property subscription to provisioning history API updates
   **/
  private apiSubscription: RxSubscription;
  /**
   * @arg content view sort element
   */
  @ViewChild(MatSort, { static: false } as any) set content(
    content: ElementRef
  ) {
    this.sort = content;
    if (this.sort && !!this.dataSource) {
      this.dataSource.sort = this.sort;
    }
  }
  /**
   * @method applyFilter method will take the filterValue and filter over the dataSourse.
   * @param string filterValue anyvalue
   */
  public applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  constructor(
    public orch: OrchestrationService,
    public translate: TranslationService,
    public apiDispatcher: ApiResponseDispatcher,
    public sidenav: SidenavService,
    private ngRedux: NgRedux<AppState>
  ) {}

  /**
   * @function ngOnInit sets initial configuration
   */
  ngOnInit() {
    this.dataSource = new MatTableDataSource([]);
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === "createDate") {
        return item.createDateRaw;
      }
      return item[property];
    };

    const apiState = this.ngRedux.getState().api;
    const initialPayload =
      apiState && apiState.apiResponse ? apiState.apiResponse["payload"] : null;

    this.apiSubscription = this.apiObservable.subscribe((api) => {
      if (api.apiAction === ApiResponseActions.SET_PROVISION_HISTORY) {
        const response = api.apiResponse;
        const payload = response && response["payload"];
        this.provisionHistory = payload && payload["content"] ? payload["content"] : [];
        this.totalCount = payload && payload["totalElements"] ? payload["totalElements"] : 0;
        this.currentPage = payload && payload["number"] !== undefined ? payload["number"] : 0;
        this.pageSize =
          (payload && payload["size"]) ||
          (payload &&
            payload["pageable"] &&
            payload["pageable"]["pageSize"]) ||
          this.pageSize;
        this.setTableData(this.formatTableData(this.provisionHistory));
      }
    });

    if (initialPayload) {
      this.provisionHistory = initialPayload["content"] ? initialPayload["content"] : [];
      this.totalCount = initialPayload["totalElements"] ? initialPayload["totalElements"] : 0;
      this.currentPage = initialPayload["number"] !== undefined ? initialPayload["number"] : 0;
      this.pageSize =
        initialPayload["size"] ||
        (initialPayload["pageable"] && initialPayload["pageable"]["pageSize"]) ||
        this.pageSize;
      this.setTableData(this.formatTableData(this.provisionHistory));
    }
  }

  /**
   * @function ngAfterViewInit Runs after view is initialized
   */
  ngAfterViewInit() {
    this.initializeSort();
  }

  /**
   * @function ngOnDestroy unsubscribes from listeners
   */
  ngOnDestroy() {
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }
  }

  /**
   * Initializes or re-initializes the sort connection and applies default sort.
   */
  private initializeSort(): void {
    setTimeout(() => {
      if (this.sort && this.dataSource) {
        this.dataSource.sort = this.sort;
        this.sort.disableClear = true;
        this.sort.active = "createDate";
        this.sort.direction = "asc";
        this.sort.sortChange.emit({ active: "createDate", direction: "asc" });
      }
    });
  }

  /**
   * @function onPageChange requests the selected provisioning history page
   * @param event paginator event
   */
  public onPageChange(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    const vin = this.getVinFromState();
    if (vin) {
      this.orch.getProvisionHistory(vin, this.currentPage, this.pageSize);
    }
  }

  /**
   * @function setTableData set table data
   * @arg provisionHistory: Array<any>
   * @returns void
   */
  public setTableData(provisionHistory: Array<any>): void {
    const tableSource = this.getTableSource(provisionHistory);
    this.dataSource.data = tableSource;
    this.displayedColumns = [
      "createDate",
      "action",
      "source",
      "dcmStatus",
      "dcmMessage",
      "headUnitStatus",
      "safetyStatus",
      "simProvisionStatus",
    ];

    this.showTable =
      this.dataSource.data && this.dataSource.data.length > 0 ? true : false;

    this.initializeSort();
  }

  /**
   * @function getTableSource get table data
   * @arg provisionHistory: Array<any>
   * @returns provisionHistory: Array<any>
   */
  public getTableSource(provisionHistory: Array<any>): Array<any> {
    return provisionHistory ? provisionHistory : [];
  }
  /**
   * @function close closes the side nav when called
   * @returns void
   **/
  public close(): void {
    const close = !!this.sidenav ? this.sidenav.close() : "";
  }
  /**
   * @function formatTableData formats the data for the table
   * @arg data
   * @returns any
   */
  private formatTableData(data): any {
    return data.map((element) => {
      return {
        createDate: element["createDate"],
        createDateRaw: new Date(element["createDate"]),
        action: !!element["action"] ? element["action"] : "",
        source: !!element["createSource"] ? element["createSource"] : "",
        dcmStatus: !!element["provisioning"]["dcm"]
          ? element["provisioning"]["dcm"]["status"]
          : "",
        dcmMessage: !!element["provisioning"]["dcm"]
          ? element["provisioning"]["dcm"]["message"]
          : "",
        headUnitStatus: !!element["provisioning"]["headUnit"]
          ? element["provisioning"]["headUnit"]["status"]
          : "",
        safetyStatus: !!element["provisioning"]["safety"]
          ? element["provisioning"]["safety"]["status"]
          : "",
        simProvisionStatus: !!element["provisioning"]["simProvision"]
          ? element["provisioning"]["simProvision"]["status"]
          : "",
      };
    });
  }
  /**
   * Retrieves data from a specific column of an element.
   * @param {Record<string, any>} element - The element object.
   * @param {string} column - The column name to retrieve data from.
   * @returns {any} - The data from the specified column.
   */
  public getCellData(element: Record<string, any>, column: string): any {
    return column === "createDate" && typeof element[column] === "string"
      ? transformDateTime(element[column])
      : element[column];
  }
  /**
   * @function getHeaderTranslation returns header translation upon recieving header column
   * @arg header: string
   * @returns headerTranslation
   **/
  public getHeaderTranslation(header: string): string {
    const headerTranslation = `provisioning.history.labels.${header}`;
    return headerTranslation;
  }

  /**
   * @function getVinFromState derives the current history VIN from redux state
   * @returns vin
   */
  private getVinFromState(): string {
    const apiState = this.ngRedux.getState().api;
    const payload =
      apiState && apiState.apiResponse ? apiState.apiResponse["payload"] : null;
    const historyItem =
      payload && payload["content"] && payload["content"].length > 0
        ? payload["content"][0]
        : null;
    return historyItem && historyItem["vin"] ? historyItem["vin"] : "";
  }
}
